import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, Search, ExternalLink } from "lucide-react";
import axios from "axios";
import { Seo } from "../components/Seo";

const API = "/api";
const SAFFRON = "#C99A2E";
const SAFFRON_LIGHT = "#F2E8DD";
const IVORY = "#FEFCF7";
const INK = "#1A1A18";
const INK_MID = "#3D3D38";
const INK_SOFT = "#6B6B63";

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function getYouTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    return u.searchParams.get("v") || "";
  } catch { return ""; }
}

export default function ConversationsPage() {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/icons`).then(res => {
      setIcons(res.data);
    }).finally(() => setLoading(false));
  }, []);

  // Flatten all episodes with their icon info
  const allEpisodes = icons.flatMap(icon =>
    (icon.episodes || []).map((ep, idx) => ({
      ...ep,
      iconName: icon.name,
      iconSlug: icon.slug,
      iconPortrait: icon.portrait_url,
      iconCategory: icon.category,
    }))
  );

  const filtered = allEpisodes.filter(ep => {
    if (!search) return true;
    const q = search.toLowerCase();
    return ep.title?.toLowerCase().includes(q) || ep.iconName?.toLowerCase().includes(q) || ep.description?.toLowerCase().includes(q);
  });

  const iconsWithEpisodes = icons.filter(i => i.episodes && i.episodes.length > 0);
  const iconsWithoutEpisodes = icons.filter(i => !i.is_anchor && (!i.episodes || i.episodes.length === 0));

  const videoJsonLd = allEpisodes.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Icons of India — Fireside Conversations",
    itemListElement: allEpisodes.slice(0, 30).map((ep, i) => ({
      "@type": "VideoObject",
      position: i + 1,
      name: ep.title,
      description: ep.description,
      thumbnailUrl: `https://img.youtube.com/vi/${getYouTubeId(ep.youtube_url || "")}/hqdefault.jpg`,
      embedUrl: `https://www.youtube.com/embed/${getYouTubeId(ep.youtube_url || "")}`,
      uploadDate: ep.added_at,
    })),
  } : null;

  // Set first video as active by default
  useEffect(() => {
    if (filtered.length > 0 && !activeVideo) {
      setActiveVideo(filtered[0]);
    }
  }, [filtered]);

  const activeVideoId = activeVideo ? getYouTubeId(activeVideo.youtube_url || "") : "";

  return (
    <div data-testid="conversations-page" className="min-h-screen" style={{ background: IVORY }}>
      {videoJsonLd && (
        <Seo
          title="Conversations — Icons of India"
          description="Fireside chats with the nation-builders of modern India. Sam Pitroda in conversation with 21 icons — watch the full films."
          path="/conversations"
          jsonLd={videoJsonLd}
        />
      )}
      {/* Hero */}
      <div style={{ background: `linear-gradient(160deg, ${INK} 0%, #2A2520 100%)`, paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <div>
              <p className="uppercase mb-4" style={{ fontSize: '11px', letterSpacing: '3px', fontWeight: 600, color: SAFFRON }}>
                Icons of India &middot; Conversations
              </p>
              <h1 className="mb-3" style={{ fontSize: 'clamp(24px, 3.5vw, 40px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500, color: '#FEFCF7', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                Fireside chats with{' '}
                <span style={{ color: SAFFRON, fontStyle: 'italic', fontFamily: "var(--iab-font-accent)" }}>India's greatest minds</span>
              </h1>
              <p className="max-w-lg" style={{ fontFamily: "var(--iab-font-accent)", fontSize: '15px', lineHeight: 1.8, color: 'rgba(254,252,247,0.5)', fontStyle: 'italic' }}>
                Hosted by Dr. Sam Pitroda. Curated by MindEscapes. Each conversation reveals the why behind a lifetime of nation-building.
              </p>
            </div>
            <a
              href="https://www.youtube.com/@iconsofindia"
              target="_blank"
              rel="noreferrer"
              data-testid="youtube-channel-link"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105"
              style={{ background: 'rgba(201,154,46,0.15)', color: SAFFRON, border: `1px solid rgba(201,154,46,0.3)` }}
            >
              <Play size={14} fill={SAFFRON} /> YouTube Channel <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: SAFFRON, borderTopColor: 'transparent' }} />
          </div>
        ) : allEpisodes.length === 0 ? (
          /* No episodes yet */
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: SAFFRON_LIGHT }}>
              <Play size={32} style={{ color: SAFFRON }} />
            </div>
            <h2 className="text-2xl font-medium mb-3" style={{ color: INK }}>Conversations Coming Soon</h2>
            <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: INK_SOFT, lineHeight: 1.7 }}>
              In-depth fireside chats with each of India's icons are being recorded and will be available here soon.
            </p>
            <a
              href="https://www.youtube.com/@iconsofindia"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all hover:scale-105"
              style={{ background: SAFFRON, color: '#fff' }}
            >
              <Play size={14} fill="#fff" /> Visit YouTube Channel
            </a>

            {/* Upcoming icons preview */}
            <div className="mt-12">
              <p className="text-xs uppercase tracking-widest font-semibold mb-6" style={{ color: SAFFRON }}>The Icons</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 max-w-3xl mx-auto">
                {icons.filter(i => !i.is_anchor).map(icon => {
                  const hasPortrait = icon.portrait_url && icon.portrait_url.length > 5;
                  return (
                    <Link
                      key={icon.id}
                      to={`/icons/${icon.slug}`}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all hover:bg-white hover:shadow-sm"
                    >
                      {hasPortrait ? (
                        <img src={icon.portrait_url} alt={icon.name} className="iab-bw-portrait w-14 h-14 rounded-full object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-full flex items-center justify-center text-sm font-medium" style={{ background: SAFFRON_LIGHT, color: SAFFRON }}>
                          {getInitials(icon.name)}
                        </div>
                      )}
                      <p className="text-xs text-center font-medium leading-tight" style={{ color: INK_MID }}>{icon.name}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Videos exist — theater layout */
          <div>
            {/* Search */}
            {allEpisodes.length > 3 && (
              <div className="mb-6">
                <div className="relative max-w-sm">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: INK_SOFT }} />
                  <input
                    data-testid="conversations-search"
                    type="text"
                    placeholder="Search conversations..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none"
                    style={{ borderColor: 'rgba(26,26,24,0.12)', background: '#fff', color: INK }}
                  />
                </div>
              </div>
            )}

            {/* Main video player + sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Active video — large player */}
              <div className="lg:col-span-2">
                {activeVideoId ? (
                  <div>
                    <div className="rounded-xl overflow-hidden" style={{ aspectRatio: '16/9', background: INK }}>
                      <iframe
                        data-testid="active-video-player"
                        src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=0&rel=0`}
                        title={activeVideo?.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                        style={{ border: 'none' }}
                      />
                    </div>
                    <div className="mt-4">
                      <h2 className="text-lg font-semibold" style={{ color: INK }}>{activeVideo?.title}</h2>
                      <div className="flex items-center gap-3 mt-2">
                        <Link to={`/icons/${activeVideo?.iconSlug}`} className="text-sm hover:underline" style={{ color: SAFFRON }}>
                          {activeVideo?.iconName}
                        </Link>
                        {activeVideo?.duration && <span className="text-xs" style={{ color: INK_SOFT }}>{activeVideo.duration}</span>}
                      </div>
                      {activeVideo?.description && (
                        <p className="mt-3 text-sm" style={{ color: INK_SOFT, lineHeight: 1.7 }}>{activeVideo.description}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl flex items-center justify-center" style={{ aspectRatio: '16/9', background: SAFFRON_LIGHT }}>
                    <Play size={48} style={{ color: SAFFRON, opacity: 0.3 }} />
                  </div>
                )}
              </div>

              {/* Sidebar — episode list */}
              <div className="lg:col-span-1">
                <h3 className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: SAFFRON }}>
                  All Conversations ({filtered.length})
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                  {filtered.map((ep, i) => {
                    const epVideoId = getYouTubeId(ep.youtube_url || "");
                    const isActive = activeVideo?.youtube_url === ep.youtube_url;
                    const hasPortrait = ep.iconPortrait && ep.iconPortrait.length > 5;
                    return (
                      <button
                        key={i}
                        data-testid={`episode-item-${i}`}
                        onClick={() => setActiveVideo(ep)}
                        className="w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all"
                        style={{
                          background: isActive ? 'rgba(201,154,46,0.08)' : '#fff',
                          border: `1px solid ${isActive ? 'rgba(201,154,46,0.25)' : 'rgba(26,26,24,0.06)'}`,
                        }}
                      >
                        {/* Thumbnail */}
                        <div className="flex-shrink-0 relative rounded-lg overflow-hidden" style={{ width: '100px', aspectRatio: '16/9', background: SAFFRON_LIGHT }}>
                          {epVideoId ? (
                            <img
                              src={`https://img.youtube.com/vi/${epVideoId}/mqdefault.jpg`}
                              alt={ep.title}
                              className="iab-bw-portrait w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play size={16} style={{ color: SAFFRON, opacity: 0.4 }} />
                            </div>
                          )}
                          {isActive && (
                            <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(201,154,46,0.35)' }}>
                              <Play size={16} fill="#fff" style={{ color: '#fff' }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold leading-snug truncate" style={{ color: isActive ? SAFFRON : INK }}>
                            {ep.title}
                          </p>
                          <p className="text-xs mt-1" style={{ color: INK_SOFT }}>{ep.iconName}</p>
                          {ep.duration && <p className="text-xs mt-0.5" style={{ color: INK_SOFT }}>{ep.duration}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* More icons coming soon */}
            {iconsWithoutEpisodes.length > 0 && (
              <div className="mt-12 pt-8" style={{ borderTop: `0.5px solid rgba(26,26,24,0.08)` }}>
                <p className="text-xs uppercase tracking-widest font-semibold mb-5" style={{ color: INK_SOFT }}>
                  More conversations coming soon
                </p>
                <div className="flex flex-wrap gap-3">
                  {iconsWithoutEpisodes.slice(0, 12).map(icon => {
                    const hasPortrait = icon.portrait_url && icon.portrait_url.length > 5;
                    return (
                      <Link
                        key={icon.id}
                        to={`/icons/${icon.slug}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-full transition-all hover:shadow-sm"
                        style={{ background: '#fff', border: '1px solid rgba(26,26,24,0.06)' }}
                      >
                        {hasPortrait ? (
                          <img src={icon.portrait_url} alt={icon.name} className="iab-bw-portrait w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ background: SAFFRON_LIGHT, color: SAFFRON }}>
                            {getInitials(icon.name)}
                          </div>
                        )}
                        <span className="text-xs font-medium" style={{ color: INK_MID }}>{icon.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
